/**
 * API 门面（真实 axios 实现）。
 * <p>
 * 8 个模块（auth/org/portal/grant/platform/user/menu/role）的函数签名与原 mock 一一对应，
 * 页面代码无需改动。成功直接返回后端响应体（无信封，对齐 mock 契约）；
 * 失败（非 2xx）后端返回 {code,message,data}，此处抛 Error(message)，由页面 catch 后 $Message 提示。
 * <p>
 * 会话：sa-token 同域共享，token 写 cookie(fsatoken)，axios withCredentials 携带；
 * dev 下 vue.config.js 将 /api 代理到 org-auth 后端(8082)，浏览器视角同源，cookie 自动随行。
 */
import axios from 'axios';
import { beginSsoRedirect } from '../js/ssoRedirect';

const http = axios.create({
	baseURL: '/api',
	timeout: 15000,
	withCredentials: true
});

// 响应拦截：2xx 取 data；非 2xx 抛 Error(message)，与 mock 的 fail(msg) 行为一致
http.interceptors.response.use(
	(resp) => resp.data,
	(err) => {
		const status = err.response && err.response.status;
		const body = err.response && err.response.data;
		const msg = body && body.message;
		const errorCode = body && body.errorCode;
		// 强制改密闸：业务 API 403 PASSWORD_CHANGE_REQUIRED → 改密页
		if (status === 403 && errorCode === 'PASSWORD_CHANGE_REQUIRED') {
			import('../router.js').then(({ default: router }) => {
				if (router.currentRoute.path !== '/force-change-password') {
					router.replace({ path: '/force-change-password' });
				}
			}).catch(() => {});
		}
		// 会话过期/未登录：硬跳登录页（SSO 场景业界标准，2026-08-17 IK95ZU）
		// 原实现用 router.replace + __ORG_AUTH_BOOT_RESTORE__ 标志，长时间停留后
		// 导航冲突/标志残留导致「提示过期但不跳转」；window.location 硬跳不受 SPA 状态影响。
		// 登录/验证码等公开接口的 401（如账号或密码错误）必须留在当前页给 Login.vue 提示，禁止硬跳。
		if (status === 401) {
			const reqUrl = (err.config && err.config.url) || '';
			const isAuthPublic = /\/auth\/(login|captcha|sms\/)/.test(reqUrl)
				|| (typeof location !== 'undefined' && /\/login(?:\/|$|\?)/.test(location.pathname));
			// 启动恢复期间(/me)的 401 交由路由守卫统一处理（守卫会跳 /login）
			if (!isAuthPublic && beginSsoRedirect()) {
				const redirect = encodeURIComponent(location.pathname + location.search);
				window.location.replace('/auth/login' + (redirect ? '?redirect=' + redirect : ''));
			}
		}
		const wrapped = new Error(msg || '请求失败，请稍后重试');
		wrapped.status = status;
		wrapped.errorCode = errorCode;
		return Promise.reject(wrapped);
	}
);

// 通用请求封装
const get = (url, params) => http.get(url, { params });
const post = (url, body) => http.post(url, body == null ? {} : body);
const put = (url, body) => http.put(url, body == null ? {} : body);
const del = (url) => http.delete(url);

// 跨系统只读请求（部署配置核查聚合）：baseURL 为空，按 nginx 路由直连各端同域路径。
// 独立实例避免触发本中心的 401 硬跳/改密闸（跨端响应格式不统一，只取 message/msg）。
const systemHttp = axios.create({
	baseURL: '',
	timeout: 15000,
	withCredentials: true
});
systemHttp.interceptors.response.use(
	(resp) => resp.data,
	(err) => {
		const status = err.response && err.response.status;
		const body = err.response && err.response.data;
		const msg = (body && (body.message || body.msg || body.error)) || (status ? ('HTTP ' + status) : '网络错误');
		return Promise.reject(new Error(msg));
	}
);
const getSystem = (path) => systemHttp.get(path);

// ---------- 鉴权 ----------
export const authApi = {
	oauth2Config: () => get('/auth/oauth2/config'),
	/** 图形验证码：{ enabled, always, captchaId, imageBase64, ttlSeconds, debugCode? } */
	captcha: () => get('/auth/captcha'),
	login: (mobile, password, captchaId, captchaCode, rememberMe) =>
		post('/auth/login', { mobile, password, captchaId, captchaCode, rememberMe }),
	logout: () => post('/auth/logout'),
	me: () => get('/auth/me'),
	menus: () => get('/auth/menus'),
	switchOrg: (orgId) => post(`/auth/switch-org/${orgId}`),
	enterAccountSet: (accountSetsId) => post(`/auth/enter-account-set/${accountSetsId}`),
	// 个人资料（自助）：查看/修改资料、修改密码
	getProfile: () => get('/auth/profile'),
	updateProfile: ({ realName, nickname, email, mobile }) =>
		put('/auth/profile', { realName, nickname, email, mobile }),
	changePassword: ({ oldPassword, newPassword }) =>
		put('/auth/profile/password', { oldPassword, newPassword }),
	getPwdChangeSmsPolicy: () => get('/auth/sms/pwd-change/policy'),
	sendPwdChangeSms: () => post('/auth/sms/pwd-change/send'),
	verifyPwdChangeSms: (code) => post('/auth/sms/pwd-change/verify', { code }),
	sendCloseAccountSetSms: () => post('/auth/sms/close-account-set/send'),
	verifyCloseAccountSetSms: (code) => post('/auth/sms/close-account-set/verify', { code }),
	/** 进入账套 MFA（IKCE0C S3） */
	sendPfLoginSms: (accountSetsId) => post('/auth/sms/pf-login/send', { accountSetsId }),
	verifyPfLoginSms: (accountSetsId, code) => post('/auth/sms/pf-login/verify', { accountSetsId, code })
};

// ---------- 组织 ----------
export const orgApi = {
	tree: (includeDeleted = false) => get('/orgs/tree', { includeDeleted }),
	detail: (orgId) => get(`/orgs/${orgId}`),
	dependencyCheck: (orgId) => get(`/orgs/${orgId}/dependency-check`),
	create: ({ name, parentId, nodeType, remark }) => post('/orgs', { name, parentId, nodeType, remark }),
	update: (orgId, patch) => put(`/orgs/${orgId}`, patch),
	/** 同级排序：direction = 'up' | 'down' */
	move: (orgId, direction) => post(`/orgs/${orgId}/move`, { direction }),
	remove: (orgId) => del(`/orgs/${orgId}`),
	/** 分页：{ list, total, page, pageSize }；含停用用户，status 区分 */
	members: (orgId, params = {}) => get(`/orgs/${orgId}/members`, {
		page: params.page == null ? 1 : params.page,
		pageSize: params.pageSize == null ? 10 : params.pageSize,
		keyword: params.keyword || undefined,
		primaryDeptId: params.primaryDeptId == null ? undefined : params.primaryDeptId
	}),
	/** primaryDeptId: null/undefined = 公司直属（通讯录挂靠，非审批职务） */
	addMember: (orgId, mobile, isOrgAdmin, isDefault, primaryDeptId) =>
		post(`/orgs/${orgId}/members`, {
			mobile,
			isOrgAdmin: !!isOrgAdmin,
			isDefault: !!isDefault,
			primaryDeptId: primaryDeptId == null || primaryDeptId === '' ? null : primaryDeptId
		}),
	removeMember: (orgId, userId) => del(`/orgs/${orgId}/members/${userId}`),
	setAdmin: (orgId, userId, isOrgAdmin) => put(`/orgs/${orgId}/members/${userId}`, { isOrgAdmin: !!isOrgAdmin }),
	setAppAdmin: (orgId, userId, appCode, enabled) =>
		put(`/orgs/${orgId}/members/${userId}/app-admin`, { appCode, enabled: !!enabled }),
	/** 调整通讯录所在部门；primaryDeptId=null 公司直属 */
	setMemberPlacement: (orgId, userId, primaryDeptId) =>
		put(`/orgs/${orgId}/members/${userId}/placement`, {
			primaryDeptId: primaryDeptId == null || primaryDeptId === '' ? null : primaryDeptId
		}),
	/** 将本组织设为该成员的默认归属组织 */
	setMemberDefault: (orgId, userId) => put(`/orgs/${orgId}/members/${userId}/default`),
	createAccountSet: (orgId, form, managerMobile, roleType = 'Manager') =>
		post(`/orgs/${orgId}/account-set`, {
			companyName: form.companyName,
			enableDate: form.enableDate,
			industry: form.industry,
			accountingStandards: form.accountingStandards,
			vatType: form.vatType,
			managerMobile,
			roleType
		}),
	/** 受控修改当前组织挂载的账套名称 */
	updateAccountSetName: (orgId, companyName) => put(`/orgs/${orgId}/account-set/name`, { companyName }),
	/** 关闭财务核算空间：断开组织-账套关联（不删账套数据） */
	closeAccountSet: (orgId) => del(`/orgs/${orgId}/account-set`)
};

// ---------- 门户：账套与做账授权 / 工作台 BFF ----------
export const voucherQuickSearchApi = {
	search: (params) => get('/finance-quick-search/vouchers', params)
};

export const portalApi = {
	workbench: () => get('/portal/workbench'),
	/** 前端先行契约：后端接线后返回已发布工作动态详情（当前后端未实现） */
	workDynamicDetail: (id) => get(`/portal/work-dynamics/${id}`),
	visibleAccountSets: (orgId) => get('/portal/account-sets', { orgId }),
	accountSetUsers: (accountSetsId) => get(`/portal/account-sets/${accountSetsId}/users`),
	grant: (accountSetsId, mobile, roleType) => post(`/portal/account-sets/${accountSetsId}/grant`, { mobile, roleType }),
	updateRole: (accountSetsId, userId, roleType) => put(`/portal/account-sets/${accountSetsId}/users/${userId}`, { roleType }),
	revoke: (accountSetsId, userId) => del(`/portal/account-sets/${accountSetsId}/users/${userId}`)
};

// ---------- 数据穿透/汇总授权（管理员台账分页；可按人/关键字/组织筛选） ----------
export const grantApi = {
	list: (params = {}) => get('/grants', params),
	add: (userId, orgId, grantType) => post('/grants', { userId, orgId, grantType }),
	remove: (id) => del(`/grants/${id}`)
};

// ---------- 平台管理 ----------
export const platformApi = {
	admins: () => get('/platform/admins'),
	addAdmin: (userId) => post('/platform/admins', { userId }),
	removeAdmin: (userId) => del(`/platform/admins/${userId}`),
	roots: (includeDeleted = false) => get('/platform/roots', { includeDeleted }),
	createRoot: (name, remark) => post('/platform/roots', { name, remark })
};

export const pmCompanyApi = {
	list: () => get('/settings/pm-companies'),
	orgs: () => get('/settings/pm-companies/orgs'),
	users: (orgId) => get('/settings/pm-companies/users', { orgId }),
	create: (body) => post('/settings/pm-companies', body),
	update: (id, body) => put(`/settings/pm-companies/${id}`, body),
	resync: (id) => post(`/settings/pm-companies/${id}/resync`)
};


// ---------- 职务与任职（P1） ----------
export const positionApi = {
	list: (includeDisabled = false) => get('/positions', { includeDisabled }),
	create: (body) => post('/positions', body),
	holdersByOrg: (orgId) => get('/positions/holders', { orgId }),
	holdersByUser: (userId) => get('/positions/holders', { userId }),
	assign: (body) => post('/positions/holders', body),
	revoke: (id) => del(`/positions/holders/${id}`),
	seedFromIsAdmin: (seedAssetAdmin = false) =>
		post(`/positions/seed-from-is-admin?seedAssetAdmin=${!!seedAssetAdmin}`)
};

// ---------- 用户 ----------
export const userApi = {
	list: ({ keyword, includeDisabled = false, page = 1, pageSize = 10 } = {}) =>
		get('/users', { keyword, includeDisabled, page, pageSize }),
	detail: (userId) => get(`/users/${userId}`),
	create: ({ mobile, realName, nickname, email, orgId, primaryDeptId, isOrgAdmin }) =>
			post('/users', {
				mobile, realName, nickname, email, orgId,
				primaryDeptId: primaryDeptId == null || primaryDeptId === '' ? null : primaryDeptId,
				isOrgAdmin: !!isOrgAdmin
			}),
	toggleStatus: (userId) => put(`/users/${userId}/status`),
	resetPassword: (userId) => put(`/users/${userId}/password`),
	/** 设置用户默认归属组织 */
	setDefaultOrg: (userId, orgId) => put(`/users/${userId}/default-org/${orgId}`)
};

// ---------- 菜单注册 ----------
export const menuApi = {
	list: () => get('/menus'),
	create: ({ id, type, name, route, icon, sort, perm }) => post('/menus', { id, type, name, route, icon, sort, perm }),
	update: (id, patch) => put(`/menus/${id}`, patch),
	remove: (id) => del(`/menus/${id}`)
};

// ---------- 角色 ----------
export const roleApi = {
	list: () => get('/roles'),
	create: ({ key, name, desc }) => post('/roles', { key, name, desc }),
	update: (key, patch) => put(`/roles/${key}`, patch),
	remove: (key) => del(`/roles/${key}`),
	menus: (key) => get(`/roles/${key}/menus`),
	setMenus: (key, menuIds) => put(`/roles/${key}/menus`, { menuIds }),
	users: (key) => get(`/roles/${key}/users`),
	setUsers: (key, userIds) => put(`/roles/${key}/users`, { userIds })
};

// ---------- 操作审计 P1 ----------
export const auditApi = {
	logs: (params) => get('/audit/logs', params),
	pilotOrgProfile: (params) => get('/audit/pilot/org-profile', params)
};

// ---------- 移动端运行时 UI 配置 ----------
export const uiConfigApi = {
	published: () => get('/admin/ui-config/published'),
	draft: () => get('/admin/ui-config/draft'),
	saveDraft: (body) => put('/admin/ui-config/draft', body),
	validate: (config) => post('/admin/ui-config/validate', { config }),
	preview: (body) => post('/admin/ui-config/preview', body),
	previewIdentity: (body) => post('/admin/ui-config/preview-identity', body),
	diagnoseIdentity: (body) => post('/admin/ui-config/diagnose-identity', body),
	submit: (id) => post('/admin/ui-config/submit', { id }),
	returnDraft: (id) => post('/admin/ui-config/return-draft', { id }),
	publish: (id) => post('/admin/ui-config/publish', { id }),
	rollback: (id) => post('/admin/ui-config/rollback', { id }),
	history: ({ page = 1, pageSize = 20 } = {}) => get('/admin/ui-config/history', { page, pageSize }),
	audit: () => get('/admin/ui-config/audit'),
	schemas: () => get('/admin/ui-config/schemas'),
	scopeOptions: () => get('/admin/ui-config/scope-options'),
	diff: (id) => get('/admin/ui-config/diff', { id }),
	assets: () => get('/admin/ui-config/assets'),
	uploadAsset: (name, file, slot) => {
		const body = new FormData();
		body.append('name', name || file.name);
		if (slot) body.append('slot', slot);
		body.append('file', file);
		return http.post('/admin/ui-config/assets', body, { headers: { 'Content-Type': 'multipart/form-data' } });
	},
	deleteAsset: (key) => del(`/admin/ui-config/assets/${key}`),
	version: (id) => get(`/platform/ui-config/versions/${id}`)
};

// ---------- 系统设置：短信网关 ----------
export const settingsApi = {
	getSmsGateway: () => get('/settings/sms-gateway'),
	updateSmsGateway: (body) => put('/settings/sms-gateway', body),
	/** 按组织「进入账套须短信」 */
	listEnterAccountSetSmsFlags: () => get('/settings/sms-gateway/enter-account-set-flags'),
	updateEnterAccountSetSmsFlag: (orgId, enterAccountSetRequireSms) =>
		put('/settings/sms-gateway/enter-account-set-flags', { orgId, enterAccountSetRequireSms }),
	/** 部署配置核查（只读，平台管理员；密钥已脱敏） */
	getDeployConfig: () => get('/settings/deploy-config'),
	/** 跨系统部署配置核查（同域直连各端；path 形如 /deploy-config、/oa/settings/deploy-config 等） */
	getSystemDeployConfig: (path) => getSystem(path)
};

// 账套角色字典（与前端展示一致；后端 RoleTitles 同源）
export const ROLES = [
	{ key: 'Manager', title: '账套管理员' },
	{ key: 'Director', title: '主管' },
	{ key: 'Making', title: '制单' },
	{ key: 'Cashier', title: '出纳' },
	{ key: 'View', title: '只看' },
	{ key: 'View_Admin', title: '全貌只读' }
];

// 行业字典（取自 public-financial 的 js/config/dict.js → industry）。
// key 为数值码（落库即此码，与 fxy_financial_account_sets.industry 同源），title 为中文展示。
export const INDUSTRIES = [
	{ key: '0', title: 'IT·通信·电子·互联网' },
	{ key: '1', title: '金融业' },
	{ key: '2', title: '房地产·建筑业' },
	{ key: '3', title: '商业服务' },
	{ key: '4', title: '贸易·批发·零售·租赁业' },
	{ key: '5', title: '文体教育·工艺美术' },
	{ key: '6', title: '生产·加工·制造' },
	{ key: '7', title: '交通·运输·物流·仓储' },
	{ key: '8', title: '服务业' },
	{ key: '9', title: '文化·传媒·娱乐·体育' },
	{ key: '10', title: '能源·矿产·环保' },
	{ key: '11', title: '政府·非盈利机构' },
	{ key: '12', title: '农·林·牧·渔·其他' }
];

// 会计准则（码位与 public-financial dict.accountingStandards 及 pf initSubject/initReport 的 standard{N} 对齐）
// 0=小企业会计准则、1=企业会计准则；2/3 预留扩展（需配套 subject/standard{N}.json 科目模板与 report/standard{N}/ 报表目录）
export const ACCOUNTING_STANDARDS = [
	{ key: '0', title: '小企业会计准则' },
	{ key: '1', title: '企业会计准则' }
];

export const serviceAccountApi = {
	usage: (params) => get('/svc-accounts/usage', params),
	options: (params) => get('/svc-accounts/usage-options', params)
};

export default { serviceAccountApi, authApi, orgApi, portalApi, grantApi, platformApi, userApi, positionApi, menuApi, roleApi, auditApi, uiConfigApi, settingsApi };
